import theme from '../../../../shared/styles/themes/default.theme'

const styles = {
  container: {
    textAlign: 'left'
  },

  skeletonCard: {
    marginBottom: 3,
    borderRadius: 2
  },

  sideSection: {
    marginBottom: 2.5
  },

  divider: {
    my: 2
  },

  contentContainer: {
    mt: 1,
    mb: 1
  },

  sectionContainer: {
    mt: 2
  },

  sectionLabel: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: 'text.secondary',
    mb: 1
  },

  // ── Avatar ──────────────────────────────────────
  avatarSx: (bgcolor: string) => ({
    bgcolor,
    width: 40,
    height: 40,
    fontSize: '0.82rem',
    fontWeight: 700,
    flexShrink: 0
  }),

  // ── Shared flex content box ──────────────────────
  flexContent: {
    flex: 1,
    minWidth: 0
  },

  // ── Person card ──────────────────────────────────
  personCard: {
    display: 'flex',
    gap: 2,
    alignItems: 'flex-start',
    p: 2,
    borderRadius: 1.5,
    bgcolor: 'action.hover',
    border: '1px solid',
    borderColor: 'divider'
  },

  personCardName: {
    mb: 0.25
  },

  personCardEmail: (hasChildren: boolean) => ({
    fontSize: '0.83rem',
    mb: hasChildren ? 1.5 : 0
  }),

  // ── Order details accordion header ──────────────
  orderDetailsHeaderRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 2,
    width: '100%',
    pr: { xs: 0, md: 1 }
  },

  orderDetailsHeaderColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0.5,
    minWidth: 0,
    flex: 1
  },

  orderDetailsHeaderSubtitleText: {
    fontSize: '0.8125rem',
    lineHeight: 1.4
  },

  orderDetailsStatusChip: {
    flexShrink: 0,
    mt: 0.25
  },

  // ── Item cards ──────────────────────────────────

  itemCard: {
    display: 'flex',
    gap: 2,
    p: 2,
    borderRadius: 1.5,
    bgcolor: 'action.hover',
    border: '1px solid',
    borderColor: 'divider',
    mb: 2,
    '&:last-child': { mb: 0 }
  },

  itemImage: {
    width: 72,
    height: 72,
    objectFit: 'cover',
    borderRadius: 1,
    flexShrink: 0
  },

  itemImagePlaceholder: {
    width: 72,
    height: 72,
    borderRadius: 1,
    bgcolor: 'action.selected',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '1.5rem'
  },

  itemPlaceholderIcon: {
    fontSize: 32,
    color: 'text.disabled',
    opacity: 0.4
  },

  itemHeaderRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 1,
    mb: 0.5
  },

  itemChipsRow: {
    display: 'flex',
    gap: 0.5,
    flexShrink: 0
  },

  badgeChip: {
    height: 20,
    fontSize: '0.68rem'
  },

  itemMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
    mt: 1.5
  },

  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
    minWidth: 60
  },

  dimensionsText: {
    fontSize: '0.8rem'
  },

  itemValueText: {
    fontSize: '0.82rem'
  },

  label: {
    fontSize: '0.72rem',
    color: 'text.disabled',
    fontWeight: 500
  },

  labelWithMargin: {
    fontSize: '0.72rem',
    color: 'text.disabled',
    fontWeight: 500,
    mb: 0.5
  },

  bodySmallText: {
    fontSize: '0.83rem'
  },

  noteBox: {
    mt: 1.5,
    p: 1.5,
    borderRadius: 1,
    bgcolor: 'action.hover',
    border: '1px solid',
    borderColor: 'divider'
  },

  // ── Recipient ────────────────────────────────────
  addressBox: {
    mt: 0.5
  },

  recipientDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1
  },

  addressText: {
    fontSize: '0.82rem',
    lineHeight: 1.8
  },

  verifiedRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    mt: 0.5
  },

  verifiedIcon: {
    fontSize: 14,
    color: 'success.main'
  },

  // ── Timeline ────────────────────────────────────
  trackingDetailsContainer: {
    padding: theme.spacing(2, 0, 0, 0)
  },

  stepper: {
    mb: 3
  },

  recentEventsDividerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    mb: 2.5
  },

  recentEventsDividerLabel: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: 'text.secondary',
    mb: 0
  },

  timelineContainer: {
    position: 'relative',
    pl: 3
  },

  timelineLine: {
    position: 'absolute',
    left: 2,
    top: 6,
    bottom: 6,
    width: '2px',
    bgcolor: 'divider',
    borderRadius: '1px'
  },

  timelineEvent: {
    position: 'relative',
    mb: 3,
    '&:last-child': { mb: 0 }
  },

  timelineDot: {
    position: 'absolute',
    left: -26,
    top: 5,
    width: 10,
    height: 10,
    borderRadius: '50%',
    bgcolor: 'primary.main',
    border: '2px solid',
    borderColor: 'background.paper',
    zIndex: 1
  },

  timelineDotFirst: {
    position: 'absolute',
    left: -26,
    top: 5,
    width: 10,
    height: 10,
    borderRadius: '50%',
    bgcolor: 'success.main',
    border: '2px solid',
    borderColor: 'background.paper',
    zIndex: 1
  },

  eventHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 0.5,
    flexWrap: 'wrap'
  },

  eventStatusChip: {
    height: 20,
    fontSize: '0.68rem',
    fontWeight: 600
  },

  eventDescriptionText: {
    fontSize: '0.83rem',
    lineHeight: 1.5
  },

  eventLocationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    mt: 0.4
  },

  locationIcon: {
    fontSize: 12,
    color: 'text.disabled'
  },

  // ── Payment receipt ─────────────────────────────
  paymentRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    py: 1.5,
    borderBottom: '1px solid',
    borderColor: 'divider',
    '&:last-of-type': { borderBottom: 'none' }
  },

  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    pt: 2,
    mt: 0.5,
    borderTop: '2px solid',
    borderColor: 'divider'
  },

  amount: {
    fontWeight: 600,
    letterSpacing: '0.02em'
  },

  paymentDeadlineText: {
    fontSize: '0.83rem'
  },

  paymentCard: {
    p: 2,
    borderRadius: 1.5,
    bgcolor: 'action.hover',
    border: '1px solid',
    borderColor: 'divider'
  }
}

export default styles
